import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Input, InputNumber, Modal, Space, Table, Tag, Typography } from 'antd'
import {
  errorNotification,
  getRequest,
  postRequest,
  successNotification,
  transactionRefundUrl
} from '../../config'
import { formatMoney } from './utils'

// Giving money back.
//
// Unlike `Tracking/RefundModal.js`, which only RECORDS a refund somebody made by hand in Paystack,
// this one asks the server to send it — and the server asks whichever company actually took the
// money. The two cases are told apart by the server, not here: `automatic: false` means nobody
// held it (merchant and bank are the customer moving money themselves), so the honest thing is a
// row for a person to action and copy that says so (companion Req 3.2).
//
// The server also owns every cap: how much is left, whether the payment settled at all, and
// whether a refund has already gone. Re-deriving any of that here is how two people refund the
// same customer twice.
const REFUND_STATUS_COLORS = {
  succeeded: 'green',
  pending: 'orange',
  failed: 'red',
  manual: 'gold'
}

// antd's `render` is a function returning elements, which `react/display-name` reads as an
// anonymous component. Same disable as `Tracking/AlertHistory.js`.
/* eslint-disable react/display-name */
const refundColumns = [
  { title: '#', dataIndex: 'sequence', key: 'sequence' },
  {
    title: 'Amount',
    key: 'amount',
    render: (_text, row) => formatMoney(row.amount, row.currency)
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: value => <Tag color={REFUND_STATUS_COLORS[value]}>{value}</Tag>
  },
  { title: 'Why / what the gateway said', dataIndex: 'gatewayMessage', key: 'gatewayMessage' },
  { title: 'By', dataIndex: 'requestedBy', key: 'requestedBy' }
]
/* eslint-enable react/display-name */

const RefundModal = ({ open, transaction, onClose, onDone }) => {
  const row = transaction || {}
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [amount, setAmount] = useState(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    if (!row.reference) return
    const response = await getRequest(
      transactionRefundUrl(row.reference),
      'the refund details',
      setLoading
    )
    if (!response.success) return
    setSummary(response.data)
    // Prefilled with everything that is left, which is what "refund this payment" means most of
    // the time. Typed down for a partial one.
    setAmount(Number(response.data.remaining))
  }, [row.reference])

  useEffect(() => {
    if (!open) return
    setReason('')
    setSaving(false)
    load()
  }, [open, load])

  const send = async () => {
    const response = await postRequest(
      transactionRefundUrl(row.reference),
      { amount, reason },
      'the refund',
      setSaving
    )
    if (!response.success) {
      // Every cap is the server's (an unsettled payment, more than is left, a payment already
      // refunded in full), and its sentence is what the operator needs to read.
      const said =
        response?.error?.response?.data?.error || response?.error?.response?.data?.detail
      errorNotification(said || 'That refund could not be sent.')
      return
    }
    const sent = response.data.refund || {}
    successNotification(
      sent.status === 'manual'
        ? 'Recorded. Somebody still has to send the money.'
        : 'Refund sent.'
    )
    onDone()
    onClose()
  }

  const submit = () => {
    if (saving || !summary || !summary.refundable) return
    const automatic = summary.automatic
    Modal.confirm({
      title: `${automatic ? 'Refund' : 'Record a refund of'} ${formatMoney(
        amount,
        summary.currency
      )}?`,
      content: automatic
        ? `This asks ${summary.gatewayLabel} to send the money back. It cannot be undone.`
        : `${summary.gatewayLabel} does not do refunds, so this records one for somebody to send by hand.`,
      okText: automatic ? 'Send the refund' : 'Record it',
      okButtonProps: { danger: automatic },
      onOk: send
    })
  }

  return (
    <Modal
      visible={open}
      title="Refund"
      onCancel={onClose}
      onOk={submit}
      okText={summary && !summary.automatic ? 'Record a refund' : 'Refund'}
      okButtonProps={{ disabled: saving || loading || !summary || !summary.refundable }}
      confirmLoading={saving}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {summary && !summary.refundable ? (
          <Alert type="info" showIcon message={summary.detail} />
        ) : null}

        {summary && summary.refundable && !summary.automatic ? (
          <Alert
            type="warning"
            showIcon
            message={`${summary.gatewayLabel} does not do refunds.`}
            description="This records a refund for somebody to send by hand. It does not move money."
          />
        ) : null}

        <div>
          <div>{`Paid ${formatMoney(summary?.amount, summary?.currency)} · already refunded ${formatMoney(
            summary?.refunded,
            summary?.currency
          )}`}</div>
          <Typography.Text type="secondary">
            {summary ? `${formatMoney(summary.remaining, summary.currency)} left` : ''}
          </Typography.Text>
        </div>

        <div>
          <div>Amount</div>
          <InputNumber
            min={0}
            step={0.01}
            precision={2}
            value={amount}
            onChange={setAmount}
            disabled={!summary || !summary.refundable}
            style={{ width: '100%' }}
          />
        </div>

        <div>
          <div>Reason</div>
          <Input.TextArea
            rows={2}
            maxLength={200}
            showCount
            value={reason}
            onChange={event => setReason(event.target.value)}
          />
        </div>

        {(summary?.refunds || []).length ? (
          <div>
            <Typography.Text strong>Already refunded</Typography.Text>
            <Table
              size="small"
              rowKey="sequence"
              columns={refundColumns}
              dataSource={summary.refunds}
              pagination={false}
            />
          </div>
        ) : null}
      </Space>
    </Modal>
  )
}

RefundModal.propTypes = {
  open: PropTypes.bool,
  transaction: PropTypes.object,
  onClose: PropTypes.func,
  onDone: PropTypes.func
}

export default RefundModal
